import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { approvePurchaseRequest, createPurchaseRequest, listPurchaseRequests } from '../../api/purchasing';
import { listItems, listUoms } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PrDetailDrawer } from '../../components/PrDetailDrawer';
import {
  BusinessText,
  Button,
  ClickableRow,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Item, PurchaseRequest, PurchaseRequestStatus, Uom } from '../../types/domain';

const PR_STATUSES: PurchaseRequestStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'CONVERTED', 'CANCELLED'];

/**
 * Purchase Requests — list, create (fn_create_purchase_request), and
 * approve (fn_approve_purchase_request) — both migration 0032,
 * permission-gated + branch-scoped. The "Approve" button only appears when
 * `hasPermission('purchase_request','approve')` is true (tenant-wide
 * prefetch); the actual click still goes through the same server function
 * that would reject a branch mismatch even if this check were somehow
 * bypassed — see fn_approve_purchase_request's own branch-resolution logic
 * (it reads the PR's own branch_id, migration 0032).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function PurchaseRequestsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [prs, setPrs] = useState<PurchaseRequest[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [selectedPr, setSelectedPr] = useState<PurchaseRequest | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('purchase_request', 'create');
  const canApprove = hasPermission('purchase_request', 'approve');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prRows, itemRows, uomRows] = await Promise.all([listPurchaseRequests(activeBranchId ?? undefined), listItems(), listUoms()]);
      setPrs(prRows);
      setItems(itemRows);
      setUoms(uomRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Re-fetches whenever the active branch changes — PR is branch-scoped
  // (purchase_requests.branch_id is a real FK), so switching "Cabang Aktif"
  // must change what this list shows.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const handleApprove = async (prId: string) => {
    setActionError(null);
    setApprovingId(prId);
    try {
      await approvePurchaseRequest(prId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setApprovingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prs.filter((pr) => {
      if (q && !pr.pr_number.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && pr.status !== statusFilter) return false;
      return true;
    });
  }, [prs, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Purchase Requests" subtitle="PR -> Approval — migration 0007/0032." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ PR baru'}
        </Button>
      )}
      {!activeBranchId && canCreate && <ErrorNote text="Belum ada branch aktif — tidak bisa membuat PR." />}
      {showForm && canCreate && activeBranchId && (
        <NewPrForm
          items={items}
          uoms={uoms}
          branchId={activeBranchId}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(prs, PR_STATUSES);
              return [
                { label: 'Total PR', value: prs.length },
                { label: 'Draft', value: c.DRAFT, tone: 'neutral' as const },
                { label: 'Submitted', value: c.SUBMITTED, tone: 'warning' as const },
                { label: 'Approved', value: c.APPROVED, tone: 'success' as const },
                { label: 'Converted', value: c.CONVERTED, tone: 'info' as const },
                { label: 'Rejected', value: c.REJECTED, tone: 'danger' as const },
                { label: 'Cancelled', value: c.CANCELLED, tone: 'neutral' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari No. PR..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="CONVERTED">Converted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
          {prs.length === 0 && <EmptyState text="Belum ada purchase request." />}
          {prs.length > 0 && total === 0 && <EmptyState text="Tidak ada PR yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. PR</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Kebutuhan</th>
                  <th style={thStyle}>Catatan</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((pr) => (
                  <ClickableRow key={pr.pr_id} onClick={() => setSelectedPr(pr)}>
                    <td style={tdStyle}>{pr.pr_number}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={pr.status} />
                    </td>
                    <td style={tdStyle}>{pr.needed_date ?? '—'}</td>
                    <td style={tdStyle}><BusinessText text={pr.notes} /></td>
                    <td style={tdStyle}>
                      {canApprove && pr.status === 'SUBMITTED' && (
                        <Button
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleApprove(pr.pr_id);
                          }}
                          loading={approvingId === pr.pr_id}
                        >
                          Approve
                        </Button>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <PrDetailDrawer pr={selectedPr} open={!!selectedPr} onClose={() => setSelectedPr(null)} />
    </div>
  );
}

interface DraftLine {
  itemId: string;
  uomId: string;
  qty: string;
}

function NewPrForm({
  items,
  uoms,
  branchId,
  onCreated,
}: {
  items: Item[];
  uoms: Uom[];
  branchId: string;
  onCreated: () => void;
}) {
  const { context } = usePermissions();
  const [neededDate, setNeededDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ itemId: '', uomId: '', qty: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, { itemId: '', uomId: '', qty: '' }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!context || !context.authenticated) return;
    setSubmitting(true);
    setError(null);
    try {
      await createPurchaseRequest({
        tenantId: context.tenant_id,
        branchId,
        neededDate: neededDate || null,
        notes: notes || null,
        lines: lines
          .filter((l) => l.itemId && l.uomId && l.qty)
          .map((l) => ({ item_id: l.itemId, uom_id: l.uomId, qty_requested: Number(l.qty) })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 180 }}>
          <FieldLabel label="Tanggal dibutuhkan">
            <TextInput type="date" value={neededDate} onChange={(e) => setNeededDate(e.target.value)} />
          </FieldLabel>
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Catatan">
            <TextInput value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FieldLabel>
        </div>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <FieldLabel label="Item">
              <Select value={line.itemId} onChange={(e) => updateLine(i, { itemId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {items.map((it) => (
                  <option key={it.item_id} value={it.item_id}>
                    {it.item_code} — {it.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 100 }}>
            <FieldLabel label="UOM">
              <Select value={line.uomId} onChange={(e) => updateLine(i, { uomId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {uoms.map((u) => (
                  <option key={u.uom_id} value={u.uom_id}>
                    {u.code}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 100 }}>
            <FieldLabel label="Qty">
              <TextInput
                type="number"
                min="0.01"
                step="0.01"
                value={line.qty}
                onChange={(e) => updateLine(i, { qty: e.target.value })}
                required
              />
            </FieldLabel>
          </div>
          {lines.length > 1 && (
            <Button type="button" variant="ghost" onClick={() => removeLine(i)}>
              Hapus
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addLine} style={{ marginBottom: 12 }}>
        + Baris
      </Button>
      <div>
        <Button type="submit" variant="primary" loading={submitting}>
          Ajukan PR
        </Button>
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
